import React, { cloneElement, isValidElement, useCallback, useContext, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { isArrayLike } from 'mobx';
import classnames from 'classnames';
import isString from 'lodash/isString';
import isFunction from 'lodash/isFunction';
import isObject from 'lodash/isObject';
import noop from 'lodash/noop';
import { Size } from '../../../lib/_util/enum';
import { pxToRem } from '../../../lib/_util/UnitConvertor';
import ConfigContext from '../../../lib/config-provider/ConfigContext';
import { ProgressStatus } from '../../../lib/progress/enum';
import Progress from '../progress/Progress';
import Icon from '../icon';
import { AttachmentButtonType } from './Attachment';
import Picture from '../picture/Picture';
import Button from '../button/Button';
import { FuncType } from '../button/enum';
import { hide, show } from '../tooltip/singleton';
import { formatFileSize } from './utils';
import Tooltip from '../tooltip/Tooltip';
import { $l } from '../locale-context';
export const ATTACHMENT_TARGET = 'attachment-preview';
const Item = function Item(props) {
    const { attachment, listType, prefixCls, onUpload, onRemove, pictureWidth: width, bucketName, onHistory, onPreview, previewTarget = ATTACHMENT_TARGET, bucketDirectory, storageCode, attachmentUUID, isCard, provided, readOnly, disabled, restCount, draggable, index, hidden, isPublic, showSize, buttons: fileButtons, getPreviewUrl: getPreviewUrlProp, } = props;
    const { status, name, filename, ext, url, size, type } = attachment;
    const { getConfig, getTooltipTheme, getTooltipPlacement } = useContext(ConfigContext);
    const attachmentConfig = getConfig('attachment');
    const tooltipRef = useRef(false);
    const pictureRef = useRef(null);
    const { getPreviewUrl: getPreviewUrlConfig, getDownloadUrl } = attachmentConfig;
    const getPreviewUrl = getPreviewUrlProp || getPreviewUrlConfig;
    const previewUrl = getPreviewUrl ? getPreviewUrl({ attachment, bucketName, bucketDirectory, storageCode, attachmentUUID, isPublic }) : url;
    const downloadUrl = getDownloadUrl && getDownloadUrl({
        attachment,
        bucketName,
        bucketDirectory,
        storageCode,
        attachmentUUID,
        isPublic,
    });
    const dragProps = { ...provided.dragHandleProps };
    const isPicture = type.startsWith('image') || ['png', 'gif', 'jpg', 'webp', 'jpeg', 'bmp', 'tif', 'pic', 'svg'].includes(ext);
    const preview = !!previewUrl && (status === 'success' || status === 'done');
    const handleOpenPreview = useCallback(async () => {
        if (isFunction(previewUrl)) {
            const result = await previewUrl();
            if (isString(result)) {
                window.open(result, previewTarget);
            }
        }
    }, [previewUrl, previewTarget]);
    const handlePreview = useCallback(() => {
        const { current } = pictureRef;
        if (current && isString(previewUrl)) {
            current.preview();
        }
        else if (isFunction(previewUrl)) {
            handleOpenPreview();
        }
    }, [pictureRef, previewUrl, handleOpenPreview]);
    const renderDragger = () => {
        if (draggable && !isCard) {
            const iconProps = {
                className: `${prefixCls}-drag-icon`,
                type: 'baseline-drag_indicator',
            };
            if (status !== 'deleting') {
                Object.assign(iconProps, dragProps);
            }
            return (React.createElement(Icon, Object.assign({}, iconProps)));
        }
    };
    const renderImagePreview = () => {
        if (listType === 'text') {
            const { renderIcon } = attachmentConfig;
            const defaultIcon = React.createElement(Icon, { type: "insert_drive_file" });
            const icon = renderIcon ? renderIcon(attachment, listType, defaultIcon) : defaultIcon;
            const isSrcIcon = isString(icon);
            if (isPicture || isSrcIcon) {
                const pictureProps = {};
                if (isString(previewUrl)) {
                    pictureProps.previewUrl = previewUrl;
                }
                else {
                    pictureProps.onClick = handleOpenPreview;
                }
                return (React.createElement(Picture, Object.assign({ width: 14, height: 14, alt: name, downloadUrl: downloadUrl, src: isSrcIcon ? icon : undefined, objectFit: "contain", status: "loaded", index: index, className: `${prefixCls}-icon`, previewTarget: isSrcIcon && !isPicture ? previewTarget : undefined, preview: preview, onPreview: onPreview, ref: pictureRef }, pictureProps), isValidElement(icon) ? icon : undefined));
            }
            if (preview) {
                const previewButtonProps = {
                    funcType: FuncType.link,
                    className: `${prefixCls}-icon`,
                };
                if (isString(previewUrl)) {
                    previewButtonProps.href = previewUrl;
                    previewButtonProps.target = previewTarget;
                }
                else {
                    previewButtonProps.onClick = handleOpenPreview;
                }
                return (React.createElement(Button, Object.assign({}, previewButtonProps), icon));
            }
            return (React.createElement("div", { className: `${prefixCls}-icon` }, icon));
        }
        if (isCard || listType === 'picture') {
            if ((preview || status === 'deleting') && isPicture) {
                return (React.createElement(Picture, { width: width, height: width, alt: name, src: isString(previewUrl) ? previewUrl : url, downloadUrl: downloadUrl, lazy: true, objectFit: "contain", index: index, preview: preview }));
            }
            return React.createElement(Picture, { width: width, height: width, alt: name, status: status === 'error' ? 'error' : 'empty', index: index });
        }
    };
    const renderPlaceholder = () => {
        if (restCount && isCard) {
            return (React.createElement("div", { className: `${prefixCls}-placeholder` },
                "+",
                restCount));
        }
    };
    const renderTitle = (isCardTitle) => {
        const fileName = (React.createElement(React.Fragment, null,
            React.createElement("span", { className: `${prefixCls}-name` }, filename),
            ext && React.createElement("span", { className: `${prefixCls}-ext` },
                ".",
                ext)));
        const nameNode = preview && listType === 'text' ? (React.createElement("a", Object.assign({}, isPicture ? { onClick: handlePreview } : isString(previewUrl) ? {
            href: previewUrl,
            target: previewTarget,
        } : { onClick: handleOpenPreview }, { className: `${prefixCls}-link` }), fileName)) : fileName;
        return (React.createElement("span", { className: `${prefixCls}-title`, style: isCardTitle ? { width: pxToRem(width) } : undefined },
            nameNode,
            !isCardTitle && showSize && React.createElement("span", { className: `${prefixCls}-size` },
                " (",
                formatFileSize(size),
                ")")));
    };
    const renderProgress = () => {
        if (status === 'uploading') {
            return (React.createElement(Progress, { value: attachment.percent || 0, size: Size.small, showInfo: false, strokeWidth: 2, className: `${prefixCls}-progress`, status: ProgressStatus.normal }));
        }
    };
    const getButtonProps = (type) => {
        const commonProps = {
            className: classnames(`${prefixCls}-icon`),
            funcType: FuncType.link,
            block: isCard,
        };
        switch (type) {
            case AttachmentButtonType.download:
                return {
                    ...commonProps,
                    icon: isCard ? 'arrow_downward' : 'get_app',
                    href: isString(downloadUrl) ? downloadUrl : undefined,
                    onClick: isFunction(downloadUrl) ? downloadUrl : noop,
                    target: previewTarget,
                };
            case AttachmentButtonType.remove:
                return {
                    ...commonProps,
                    icon: isCard ? 'delete_forever-o' : 'close',
                    onClick: () => onRemove(attachment),
                };
            case AttachmentButtonType.history:
                if (onHistory && attachmentUUID) {
                    return {
                        ...commonProps,
                        icon: 'library_books',
                        onClick: () => onHistory(attachment, attachmentUUID),
                    };
                }
                return { onClick: noop };
            default:
                break;
        }
    };
    const renderButtons = () => {
        const buttons = [];
        if (!readOnly && status === 'error' && !attachment.invalid) {
            const upProps = {
                key: 'upload',
                className: classnames(`${prefixCls}-icon`),
                icon: 'replay',
                onClick: () => onUpload(attachment),
                funcType: FuncType.link,
                block: isCard,
            };
            buttons.push(React.createElement(Button, Object.assign({}, upProps)));
        }
        fileButtons.forEach(btn => {
            let btnProps = {};
            if (isArrayLike(btn)) {
                btnProps = btn[1] || {};
                btn = btn[0];
            }
            if (isString(btn) && btn in AttachmentButtonType) {
                const defaultButtonProps = getButtonProps(btn);
                if (defaultButtonProps) {
                    const index = fileButtons.indexOf(btn);
                    switch (btn) {
                        case AttachmentButtonType.history:
                            if (attachmentUUID && onHistory && (!status || status === 'success' || status === 'done')) {
                                buttons.splice(index, 1, React.createElement(Tooltip, { key: btn, title: $l('Attachment', 'view_operation_records') },
                                    React.createElement(Button, Object.assign({}, defaultButtonProps, btnProps))));
                            }
                            break;
                        case AttachmentButtonType.download:
                            if (downloadUrl && (!status || status === 'success' || status === 'done')) {
                                buttons.splice(index, 1, React.createElement(Tooltip, { key: btn, title: $l('Attachment', 'download') },
                                    React.createElement(Button, Object.assign({}, defaultButtonProps, btnProps))));
                            }
                            break;
                        case AttachmentButtonType.remove:
                            if (attachmentUUID && status !== 'uploading') {
                                buttons.splice(index, 1, React.createElement(Tooltip, { key: btn, title: status === 'deleting' ? undefined : $l('Attachment', 'delete') },
                                    React.createElement(Button, Object.assign({ hidden: readOnly || disabled }, defaultButtonProps, btnProps))));
                            }
                            break;
                        default:
                            break;
                    }
                }
            }
            else if (isValidElement(btn)) {
                buttons.push(cloneElement(btn, { ...btn.props }));
            }
            else if (isObject(btn)) {
                buttons.push(React.createElement(Button, Object.assign({}, btn)));
            }
        });
        if (buttons.length) {
            return (React.createElement("div", { className: classnames(`${prefixCls}-buttons`, { [`${prefixCls}-buttons-visible`]: status === 'deleting' }) }, buttons));
        }
    };
    const renderErrorMessage = () => {
        if (status === 'error') {
            const { errorMessage } = attachment;
            if (errorMessage) {
                return (React.createElement("div", { className: `${prefixCls}-error-content` },
                    React.createElement(Icon, { type: "warning" }),
                    React.createElement("span", { className: `${prefixCls}-error-message` }, errorMessage)));
            }
        }
    };
    const errorMessageNode = renderErrorMessage();
    const handleMouseEnter = useCallback((e) => {
        if (errorMessageNode) {
            show(e.currentTarget, {
                title: errorMessageNode,
                theme: getTooltipTheme('validation'),
                placement: getTooltipPlacement('validation') || 'bottomLeft',
            });
            tooltipRef.current = true;
        }
    }, [errorMessageNode, getTooltipTheme, getTooltipPlacement, tooltipRef]);
    const handleMouseLeave = useCallback(() => {
        if (tooltipRef.current) {
            hide();
            tooltipRef.current = false;
        }
    }, [tooltipRef]);
    useEffect(() => () => {
        if (tooltipRef.current) {
            hide();
            tooltipRef.current = false;
        }
    }, []);
    const listProps = {
        ref: provided.innerRef,
        className: classnames(prefixCls, {
            [`${prefixCls}-error`]: status === 'error',
            [`${prefixCls}-success`]: status === 'success',
        }),
        ...provided.draggableProps,
        style: {
            ...provided.draggableProps.style,
        },
    };
    if (draggable && isCard) {
        Object.assign(listProps, dragProps);
    }
    return (React.createElement("div", Object.assign({}, listProps, { hidden: hidden }),
        React.createElement("div", { className: `${prefixCls}-container`, onMouseEnter: isCard ? handleMouseEnter : undefined, onMouseLeave: isCard ? handleMouseLeave : undefined },
            React.createElement("div", { className: `${prefixCls}-content` },
                renderDragger(),
                renderImagePreview(),
                renderPlaceholder(),
                !restCount && !isCard && renderTitle(),
                !restCount && renderButtons()),
            errorMessageNode,
            renderProgress()),
        !restCount && isCard && renderTitle(true)));
};
Item.displayName = 'Item';
export default observer(Item);
